/**
 * The payout queue: assistants ask, an admin sends the money, and records it
 * here.
 *
 * Paystack refuses third-party transfers while the business profile is a
 * starter account, so payouts are made by hand from the bank for now. The
 * money leaves the assistant's withdrawable balance the moment they ask, so
 * nothing here can be spent twice while it waits — and declining a request is
 * what puts it back.
 */
import { useEffect, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  RefreshCw,
} from "lucide-react";
import AdminLayout from "../../components/admin/AdminLayout";
import {
  useWithdrawalsQuery,
  useApproveWithdrawalMutation,
  useMarkWithdrawalPaidMutation,
  useDeclineWithdrawalMutation,
  useRevertWithdrawalMutation,
} from "../../api/query/useAdminQueries";
import { getApiErrorMessage } from "../../utils/apiError";

const PAGE_SIZE = 20;

const STATUS_FILTERS = [
  { value: "pending", label: "To pay" },
  { value: "processing", label: "Being processed" },
  { value: "success", label: "Paid" },
  { value: "failed", label: "Declined" },
  { value: "", label: "All" },
];

const STATUS_STYLES = {
  success: "bg-[#E4F5E4] text-[#008000]",
  processing: "bg-[#E8F0FE] text-[#1A56DB]",
  pending: "bg-[#FFF4DB] text-[#F99C07]",
  failed: "bg-[#FDE8E8] text-[#C81E1E]",
};

const STATUS_LABELS = {
  success: "Paid",
  processing: "Being processed",
  pending: "Awaiting payout",
  failed: "Declined",
};

const formatNaira = (amount) =>
  `₦${Number(amount || 0).toLocaleString("en-NG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const assistantName = (user) => {
  if (!user || typeof user !== "object") return "Unknown assistant";
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
  return name || user.email || "Unknown assistant";
};

function AdminWithdrawals() {
  // Opens on the queue an admin actually has to work through.
  const [status, setStatus] = useState("pending");
  const [page, setPage] = useState(1);
  // { withdrawal, mode: "mark-paid" | "decline" }
  const [prompt, setPrompt] = useState(null);
  // The row whose action menu is open, with the screen position to draw it at.
  // Fixed positioning rather than absolute: the table scrolls inside its own
  // box, which would clip a menu opened on the last row.
  const [menu, setMenu] = useState(null);
  const [promptValue, setPromptValue] = useState("");
  const [actionError, setActionError] = useState("");

  const approve = useApproveWithdrawalMutation();
  const markPaid = useMarkWithdrawalPaidMutation();
  const decline = useDeclineWithdrawalMutation();
  const revert = useRevertWithdrawalMutation();
  const isActing =
    approve.isPending || markPaid.isPending || decline.isPending || revert.isPending;

  const { data, isLoading, isError, error, refetch, isFetching } = useWithdrawalsQuery({
    ...(status ? { status } : {}),
    page,
    limit: PAGE_SIZE,
  });

  const payload = data?.data || data || {};
  const withdrawals = Array.isArray(payload.items) ? payload.items : [];
  const pagination = payload.pagination || {};
  const totalPages = pagination.pages || 1;
  const total = pagination.total || 0;

  const changeStatus = (value) => {
    setStatus(value);
    setPage(1);
  };

  // Anything that moves the table moves the button the menu is pinned to, so
  // the menu closes rather than floating somewhere it no longer belongs.
  useEffect(() => {
    if (!menu) return undefined;

    const close = () => setMenu(null);
    const onKeyDown = (event) => {
      if (event.key === "Escape") close();
    };

    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menu]);

  const toggleMenu = (event, withdrawal) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setMenu((current) =>
      current?.id === withdrawal.transactionId
        ? null
        : {
            id: withdrawal.transactionId,
            withdrawal,
            top: rect.bottom + 8,
            right: Math.max(16, window.innerWidth - rect.right),
          }
    );
  };

  const openPrompt = (withdrawal, mode) => {
    setMenu(null);
    setPrompt({ withdrawal, mode });
    setPromptValue("");
    setActionError("");
  };

  const closePrompt = () => {
    setPrompt(null);
    setPromptValue("");
    setActionError("");
  };

  const runAction = async (fn) => {
    setActionError("");
    try {
      await fn();
      closePrompt();
    } catch (error) {
      setActionError(getApiErrorMessage(error, "That did not go through. Try again."));
    }
  };

  const handleApprove = (withdrawal) => {
    setMenu(null);
    return runAction(() => approve.mutateAsync(withdrawal.transactionId));
  };

  const submitPrompt = () => {
    if (!prompt) return;
    const id = prompt.withdrawal.transactionId;

    if (prompt.mode === "decline") {
      if (!promptValue.trim()) {
        setActionError("Tell the assistant why, so they know what to fix.");
        return;
      }
      return runAction(() =>
        decline.mutateAsync({ withdrawalId: id, reason: promptValue.trim() })
      );
    }

    if (prompt.mode === "revert") {
      if (!promptValue.trim()) {
        setActionError("Say what was wrong, so the record explains itself later.");
        return;
      }
      return runAction(() =>
        revert.mutateAsync({ withdrawalId: id, reason: promptValue.trim() })
      );
    }

    return runAction(() =>
      markPaid.mutateAsync({ withdrawalId: id, paymentReference: promptValue.trim() })
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Withdrawals</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-600">
            Assistants request payouts here and an admin sends the money from
            the bank, while Paystack transfers are unavailable. Declining a
            request returns the money to their wallet.
          </p>
        </div>

        <section className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
          <div className="flex flex-col gap-4 border-b border-gray-200 px-6 py-5 md:flex-row md:items-center md:justify-between">
            <h2 className="text-xl font-bold text-gray-900">
              {total} withdrawal{total === 1 ? "" : "s"}
            </h2>

            <div className="flex flex-wrap items-center gap-2">
              {STATUS_FILTERS.map((filter) => (
                <button
                  key={filter.value || "all"}
                  type="button"
                  onClick={() => changeStatus(filter.value)}
                  className={`rounded-full border px-4 py-2 text-sm font-medium transition ${
                    status === filter.value
                      ? "border-[#570033] bg-[#570033] text-white"
                      : "border-[#C9D3E2] bg-white text-gray-700 hover:border-[#570033]"
                  }`}
                >
                  {filter.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => refetch()}
                disabled={isFetching}
                className="flex items-center gap-2 rounded-full border border-[#C9D3E2] bg-white px-4 py-2 text-sm font-medium text-gray-700 transition hover:border-[#570033] disabled:opacity-60"
              >
                <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
                Refresh
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-gray-200">
                  {[
                    "Assistant",
                    "Amount",
                    "Destination",
                    "Status",
                    "Requested",
                    "Reference",
                    "Action",
                  ].map((heading) => (
                    <th
                      key={heading}
                      className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.08em] text-gray-500"
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-10 text-center text-sm text-gray-500">
                      Loading withdrawals...
                    </td>
                  </tr>
                ) : isError ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-10 text-center text-sm text-red-600">
                      {error?.message || "Unable to load withdrawals."}
                    </td>
                  </tr>
                ) : withdrawals.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-10 text-center text-sm text-gray-500">
                      No withdrawals to show.
                    </td>
                  </tr>
                ) : (
                  withdrawals.map((withdrawal) => (
                    <tr
                      key={withdrawal._id || withdrawal.transactionId}
                      className="border-b border-gray-100 last:border-0"
                    >
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {assistantName(withdrawal.user)}
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                        {formatNaira(withdrawal.amount)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        <span className="block">{withdrawal.bank?.bankName || "—"}</span>
                        <span className="block text-xs text-gray-400">
                          {withdrawal.bank?.accountNumber || ""}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span
                          className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                            STATUS_STYLES[withdrawal.status] || "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {STATUS_LABELS[withdrawal.status] || withdrawal.status}
                        </span>
                        {withdrawal.failureReason ? (
                          <span className="mt-1 block max-w-[260px] text-xs leading-4 text-gray-500">
                            {withdrawal.failureReason}
                          </span>
                        ) : null}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {formatDate(withdrawal.createdAt)}
                      </td>
                      <td className="px-6 py-4 text-xs font-medium text-gray-500">
                        <span className="block">{withdrawal.transactionId}</span>
                        {withdrawal.paymentReference ? (
                          <span className="mt-1 block text-[11px] text-gray-400">
                            Bank ref: {withdrawal.paymentReference}
                          </span>
                        ) : null}
                      </td>
                      <td className="px-6 py-4">
                        {(
                          <button
                            type="button"
                            aria-label={`Actions for ${withdrawal.transactionId}`}
                            aria-haspopup="menu"
                            aria-expanded={menu?.id === withdrawal.transactionId}
                            onMouseDown={(event) => event.stopPropagation()}
                            onClick={(event) => toggleMenu(event, withdrawal)}
                            disabled={isActing}
                            className={`rounded-full border p-2 transition disabled:opacity-50 ${
                              menu?.id === withdrawal.transactionId
                                ? "border-[#570033] bg-[#F8EEF4] text-[#570033]"
                                : "border-transparent text-gray-500 hover:border-[#C9D3E2] hover:text-gray-700"
                            }`}
                          >
                            <MoreVertical className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 ? (
            <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4">
              <span className="text-sm text-gray-500">
                Page {pagination.page || page} of {totalPages}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  disabled={(pagination.page || page) <= 1}
                  className="flex items-center gap-1 rounded-full border border-[#C9D3E2] px-3 py-2 text-sm text-gray-700 transition hover:border-[#570033] disabled:opacity-50"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={(pagination.page || page) >= totalPages}
                  className="flex items-center gap-1 rounded-full border border-[#C9D3E2] px-3 py-2 text-sm text-gray-700 transition hover:border-[#570033] disabled:opacity-50"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          ) : null}
        </section>
      </div>

      {menu ? (
        <div
          role="menu"
          onMouseDown={(event) => event.stopPropagation()}
          style={{ top: menu.top, right: menu.right }}
          className="fixed z-50 w-48 overflow-hidden rounded-xl border border-gray-100 bg-white py-1 shadow-lg"
        >
          {menu.withdrawal.status === "pending" ? (
            <button
              type="button"
              role="menuitem"
              onClick={() => handleApprove(menu.withdrawal)}
              className="flex w-full items-center px-4 py-2.5 text-left text-sm text-gray-700 transition hover:bg-gray-50"
            >
              Approve
            </button>
          ) : null}

          {menu.withdrawal.status === "pending" ||
          menu.withdrawal.status === "processing" ? (
            <>
              <button
                type="button"
                role="menuitem"
                onClick={() => openPrompt(menu.withdrawal, "mark-paid")}
                className="flex w-full items-center px-4 py-2.5 text-left text-sm font-semibold text-[#570033] transition hover:bg-gray-50"
              >
                Mark as paid
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => openPrompt(menu.withdrawal, "decline")}
                className="flex w-full items-center px-4 py-2.5 text-left text-sm text-red-600 transition hover:bg-red-50"
              >
                Decline
              </button>
            </>
          ) : null}

          {menu.withdrawal.status === "pending" ? null : (
            <button
              type="button"
              role="menuitem"
              onClick={() => openPrompt(menu.withdrawal, "revert")}
              className="flex w-full items-center border-t border-gray-100 px-4 py-2.5 text-left text-sm text-gray-700 transition hover:bg-gray-50"
            >
              Revert status
            </button>
          )}
        </div>
      ) : null}

      {prompt ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-bold text-gray-900">
              {prompt.mode === "decline"
                ? "Decline this payout"
                : prompt.mode === "revert"
                ? "Revert this status"
                : "Record the payment"}
            </h3>
            <p className="mt-2 text-sm leading-6 text-gray-600">
              {prompt.mode === "decline"
                ? `${formatNaira(prompt.withdrawal.amount)} goes back to ${assistantName(
                    prompt.withdrawal.user
                  )}'s wallet, and they are told why.`
                : prompt.mode === "revert"
                ? prompt.withdrawal.status === "failed"
                  ? `This puts the request back in the queue and takes the refunded ${formatNaira(
                      prompt.withdrawal.amount
                    )} back out of their wallet. It will not go through if they have already withdrawn it.`
                  : `This puts the request back into the queue as unpaid work. No money moves — it left their balance when they asked.`
                : `Confirm you have sent ${formatNaira(
                    prompt.withdrawal.amount
                  )} to ${prompt.withdrawal.bank?.bankName || "their bank"} ${
                    prompt.withdrawal.bank?.accountNumber || ""
                  }.`}
            </p>

            <label className="mt-4 block text-xs font-semibold text-gray-600">
              {prompt.mode === "decline"
                ? "Reason"
                : prompt.mode === "revert"
                ? "What was wrong?"
                : "Bank reference (optional)"}
              <input
                type="text"
                value={promptValue}
                onChange={(e) => setPromptValue(e.target.value)}
                placeholder={
                  prompt.mode === "decline"
                    ? "Account name does not match their profile"
                    : prompt.mode === "revert"
                    ? "Recorded against the wrong request"
                    : "e.g. GTB-TRF-99812"
                }
                className="mt-2 w-full rounded-lg border border-[#C9D3E2] px-3 py-2 text-sm text-gray-700 outline-none focus:border-[#570033]"
              />
            </label>

            {actionError ? (
              <p className="mt-3 text-sm text-red-600">{actionError}</p>
            ) : null}

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={closePrompt}
                disabled={isActing}
                className="rounded-full border border-[#C9D3E2] px-4 py-2 text-sm font-semibold text-gray-700 transition hover:border-[#570033] disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitPrompt}
                disabled={isActing}
                className={`rounded-full px-4 py-2 text-sm font-semibold text-white transition disabled:opacity-50 ${
                  prompt.mode === "decline"
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-[#570033] hover:bg-[#450029]"
                }`}
              >
                {isActing
                  ? "Working..."
                  : prompt.mode === "decline"
                  ? "Decline and refund"
                  : prompt.mode === "revert"
                  ? "Revert status"
                  : "Mark as paid"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </AdminLayout>
  );
}

export default AdminWithdrawals;
